import { ChangeEvent, useCallback, useMemo, useState } from "react";
import { DropDownForm } from "src/components/common/form/DropdownForm";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import useUiuxStore from "src/store/useUiuxStore";

import { getEmployeeList } from "src/apis/employeeApi";
import { BRDiv } from "src/components/common/common";
import { CodeForm } from "src/components/common/form/CodeForm";
import { InputForm } from "src/components/common/form/input/InputForm";
import { SingleDateForm } from "src/components/common/form/SingleDateForm";
import { useInfoFormEvents } from "src/hooks/useInfoFormEvents";
import { InfoCodeSearchFieldConfig, InfoFieldHandlers } from "src/types/info-form";
import { formatDate } from "src/utils/string/stringUtil";
import { PRD3012_R01HeaderDto, PRD3012_R01LotDto } from "./Dto_PRD3012_R01";
import { usePRD3012_R01Store, calculateLotQty } from "./Store_PRD3012_R01";

interface Props {
	defaultPRD3012_R01HeaderRow: PRD3012_R01HeaderDto;
	defaultPRD3012_R01LotRow: PRD3012_R01LotDto;
	getProductWorkFieldHandlers: (fieldName: string) => InfoFieldHandlers;
	handleFetchProductResult: () => Promise<void>;
	handleLotCreateClick: () => Promise<void>;
}

export const ResultInfo_PRD3012_R01 = (props: Props) => {
	const {
		defaultPRD3012_R01HeaderRow,
		defaultPRD3012_R01LotRow,
		getProductWorkFieldHandlers,
		handleFetchProductResult,
		handleLotCreateClick,
	} = props;

	const { jobTeamList } = useCommonCodeStore();
	const { setCodeSearchModalProps } = useUiuxStore();
	const {
		editPRD3012_R01, setEditPRD3012_R01,
		PRD3012_R01LotList,
		deletedPRD3012_R01LotList,
		modalVisible, setModalVisible,
	} = usePRD3012_R01Store();

	const totalLotQty = useMemo(
		() => calculateLotQty(PRD3012_R01LotList, deletedPRD3012_R01LotList),
		[PRD3012_R01LotList, deletedPRD3012_R01LotList],
	);

	const lotQtyInputValue = editPRD3012_R01?.pendingLotQty ?? 0;

	const [productDtPickerVisible, setProductDtPickerVisible] = useState(false);
	const [productDtPickerPos, setProductDtPickerPos] = useState<{ top: number; left: number; height: number }>({
		top: 0, left: 0, height: 0,
	});

	const workEmpInfoHandleFoucsId = 'resultInfoWorkEmpInput';

	const infoFieldConfigs: InfoCodeSearchFieldConfig[] = [
		{
			fieldName: 'empNo',
			category: '담당자',
			focusId: workEmpInfoHandleFoucsId,
			responseMapping: {
				empNo: 'productEmp',
				empNm: 'productEmpNm'
			},
			searchFunction: async (code: string) => {
				const employeeResponse = await getEmployeeList('all');
				return employeeResponse.data.data.find(value => value.empNo === code);
			}
		},
	];

	const { getFieldHandlers } = useInfoFormEvents({
		editData: editPRD3012_R01,
		setEditData: setEditPRD3012_R01,
		defaultData: defaultPRD3012_R01HeaderRow,
		infoFieldConfigs,
		setCodeSearchModalProps,
		formName: 'PRD3012_R01_ResultInfo'
	});

	const handleDateChange = useCallback((field: keyof PRD3012_R01HeaderDto, value: any) => {
		setEditPRD3012_R01({
			...defaultPRD3012_R01HeaderRow,
			...editPRD3012_R01,
			[field]: value
		});
	}, [editPRD3012_R01, defaultPRD3012_R01HeaderRow, setEditPRD3012_R01]);

	const handleChange = useCallback((e: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLSelectElement>) => {
		const { name, value } = e.target;
		setEditPRD3012_R01({
			...defaultPRD3012_R01HeaderRow,
			...editPRD3012_R01,
			[name]: value,
		});
	}, [editPRD3012_R01, defaultPRD3012_R01HeaderRow, setEditPRD3012_R01]);

	const handleLotQtyChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
		const lotQty = Number(e.target.value) || 0;
		setEditPRD3012_R01({
			...defaultPRD3012_R01HeaderRow,
			...editPRD3012_R01,
			pendingLotQty: lotQty,
		});
	}, [editPRD3012_R01, defaultPRD3012_R01HeaderRow, setEditPRD3012_R01]);

	const handleProductResultSearchClick = useCallback(() => {
		if (!editPRD3012_R01?.productResultNo) {
			getProductWorkFieldHandlers('productResultNo')
				.onDoubleClick({ currentTarget: { blur: () => {}, value: '' } } as unknown as React.MouseEvent<HTMLInputElement>);
			return;
		}
		void handleFetchProductResult();
	}, [editPRD3012_R01?.productResultNo, getProductWorkFieldHandlers, handleFetchProductResult]);

	const shortInputFormWrapperOption = {
		wrapperStyle: "w-[230px] min-w-[230px] mb-[5px]",
		titleStyle: "w-[100px] min-w-[100px] text-left",
		inputStyle: "w-[120px] min-w-[120px]",
	};

	const inputFormWrapperOption = {
		wrapperStyle: "w-[230px] min-w-[230px] mb-[5px]",
		titleStyle: "w-[100px] min-w-[100px] text-left",
		codeInputStyle: "w-[120px] min-w-[120px]",
		custNmInputStyle: "w-[105px] min-w-[105px]",
	};

	return (
		<div className="w-full h-full flex flex-col gap-[4px]">
			<div className="h-full max-h-full flex flex-wrap content-start overflow-y-auto">
				{/* 작업지시 InputForm과 동일 패턴 — 생산실적코드 */}
				<div className="w-full flex flex-wrap items-center">
					<CodeForm
						label="생산실적코드"
						value={editPRD3012_R01?.productResultNo || ""}
						custNmValue={editPRD3012_R01?.productOrderNo || ""}
						editable={true}
						custNmInputless={true}
						name="productResultNo"
						handleChange={handleChange}
						onDoubleClick={(e) => {
							getProductWorkFieldHandlers('productResultNo').onDoubleClick(e);
						}}
						onKeyDown={(e) => {
							getProductWorkFieldHandlers('productResultNo').onEnterKeyDown(e);
						}}
						wrapperOption={inputFormWrapperOption}
					/>
					<button
						className="text-white bg-blue-dark items-center justify-center px-[8px] py-[2px] rounded-[4px] w-[74px] mb-[4px] flex-shrink-0"
						onClick={handleProductResultSearchClick}
					>
						조회
					</button>
				</div>
				<BRDiv />
				<SingleDateForm
					title="생산일자"
					value={editPRD3012_R01?.productDt ? new Date(editPRD3012_R01.productDt) : undefined}
					setValue={(value: Date | undefined) => handleDateChange('productDt', formatDate(value))}
					editable={false}
					visible={productDtPickerVisible} setVisible={setProductDtPickerVisible}
					onChange={handleChange}
					pickerPos={productDtPickerPos} setPickerPos={setProductDtPickerPos}
				/>
				<BRDiv />
				<DropDownForm
					title="작업팀"
					name="jobTeam"
					initLabelLess={true}
					value={editPRD3012_R01?.jobTeam}
					data={jobTeamList}
					editable={true}
					onChange={handleChange}
				/>
				<BRDiv />
				<CodeForm
					label="작업자"
					value={editPRD3012_R01?.productEmp || ""}
					custNmValue={editPRD3012_R01?.productEmpNm || ""}
					editable={true}
					name="productEmp"
					handleChange={handleChange}
					onDoubleClick={getFieldHandlers('empNo').onDoubleClick}
					onKeyDown={getFieldHandlers('empNo').onEnterKeyDown}
				/>
				<BRDiv />
				<div className="w-full flex flex-wrap items-center">
					<InputForm title="생산량" value={lotQtyInputValue}
						editable={true}
						name="pendingLotQty"
						onChange={handleLotQtyChange}
						wrapperOption={shortInputFormWrapperOption}
					/>
					<button
						type="button"
						className="text-white bg-blue-dark items-center justify-center px-[8px] py-[2px] rounded-[4px] w-[74px] mb-[4px] flex-shrink-0"
						onClick={() => void handleLotCreateClick()}
					>
						LOT 발행
					</button>
				</div>
				<BRDiv />
				<div className="w-full flex flex-wrap items-center">
					<InputForm title="생산불량수량" value={editPRD3012_R01?.badQty || 0}
						editable={false}
						name="badQty"
						onChange={handleChange}
						wrapperOption={shortInputFormWrapperOption}
					/>
					<button
						type="button"
						className="text-white bg-blue-dark items-center justify-center px-[8px] py-[2px] rounded-[4px] w-[74px] mb-[4px] flex-shrink-0"
						onClick={() => setModalVisible(!modalVisible)}
					>
						불량내역
					</button>
				</div>
				<BRDiv />
				<InputForm title="총생산량" value={totalLotQty}
					editable={false}
					name="totalLotQty"
					onChange={handleChange} />
				<BRDiv />
				<InputForm title="실적 Count" value={editPRD3012_R01?.plcCount ?? 0}
					editable={false}
					name="plcCount"
					onChange={handleChange} />
			</div>
		</div>
	);
};
